import { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Bus,
  MapPin,
  Bell,
  LayoutDashboard,
  ShieldCheck,
  Users,
  CheckCircle2,
  ArrowRight,
  Clock,
  Smartphone,
  Menu,
  X,
  Zap,
  Navigation,
  UserCheck,
  TrendingDown,
  Mail,
  School,
  Facebook,
  Linkedin,
  Youtube,
} from 'lucide-react';
import {
  motion,
  AnimatePresence,
  animate,
  useScroll,
  useTransform,
  useReducedMotion,
  useMotionValue,
  useMotionValueEvent,
  useVelocity,
  useSpring,
} from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { AppStoreBadge, GooglePlayBadge, StoreBadgeLink } from '@/components/StoreBadges';
import { WhatsAppButton } from '@/components/WhatsAppButton';
import { cn } from '@/lib/utils';
import unifiedApi from '../services/unifiedApi';

const STORE_LINKS = {
  googlePlay: 'https://play.google.com/store/apps/details?id=com.schoolbus.simple',
  appStore: 'https://apps.apple.com/fr/app/meyebus/id6760791310',
};

const SOCIAL_LINKS = [
  { href: 'https://www.linkedin.com/company/m-eyebus/', label: 'LinkedIn', Icon: Linkedin },
  { href: 'https://www.facebook.com/people/MEyeBus/61593684677025/', label: 'Facebook', Icon: Facebook },
  { href: 'https://www.youtube.com/@MEyeBus', label: 'YouTube', Icon: Youtube },
];

// Icônes réseaux sociaux — partagées par la Navbar (à côté du bouton WhatsApp) et le Footer.
const SocialLinks = ({ className, itemClassName }) => (
  <div className={cn('flex items-center gap-2', className)}>
    {SOCIAL_LINKS.map(({ href, label, Icon }) => (
      <a
        key={label}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={`M'EyeBus sur ${label}`}
        className={cn(
          'flex items-center justify-center border-slate-200 bg-white text-slate-500 transition-colors hover:border-blue-600 hover:text-blue-600',
          itemClassName,
        )}
      >
        <Icon className="h-4 w-4" aria-hidden="true" />
      </a>
    ))}
  </div>
);

const scrollToContact = () => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });

// --- Compteur animé (déclenché une fois au scroll) — logique partagée par Counter et DriverExperienceSection ---
const useCountUp = (target, { duration = 1.5, ease = 'easeOut' } = {}) => {
  const [display, setDisplay] = useState(0);
  const shouldReduceMotion = useReducedMotion();

  const start = () => {
    if (shouldReduceMotion) {
      setDisplay(target);
      return;
    }
    const controls = animate(0, target, {
      duration,
      ease,
      onUpdate: (v) => setDisplay(Math.round(v)),
    });
    return () => controls.stop();
  };

  return [display, start];
};

const Counter = ({ value, prefix = '', suffix = '', duration = 1.5 }) => {
  const [display, start] = useCountUp(value, { duration });

  return (
    <motion.span onViewportEnter={start} viewport={{ once: true, amount: 0.6 }}>
      {prefix}
      {new Intl.NumberFormat('fr-FR').format(display)}
      {suffix}
    </motion.span>
  );
};

// --- Fil de scroll narratif (storytelling) — desktop uniquement, désactivé si reduced-motion ---
// Rail borné au viewport (top-24/bottom-24) pour ne jamais déborder sous la navbar ni dans le footer.
// Un arrêt par section directe de <main>, espacés régulièrement sur le rail ; le bus y avance de façon
// continue (ressort) et « arrive » à un arrêt quand le haut de la section atteint SCROLL_THREAD.reachAt
// du viewport. Terminus = l'école (section contact).
const SCROLL_THREAD = {
  reachAt: 0.4, // fraction de hauteur de viewport où le haut d'une section compte comme « atteint »
  minGap: 0.01, // écart mini de scroll entre deux arrêts (évite les arrêts empilés en fin de page)
  minGutterPx: 48, // marge gauche mini du contenu pour afficher le rail sans le chevaucher
  labelIdleMs: 1400, // le libellé disparaît après ce délai sans scroll
  spring: { stiffness: 140, damping: 26, mass: 0.6 },
};

// Interpolation linéaire par morceaux (entrées strictement croissantes).
const interpolatePiecewise = (inputs, outputs, v) => {
  if (inputs.length < 2) return 0;
  if (v <= inputs[0]) return outputs[0];
  for (let i = 1; i < inputs.length; i++) {
    if (v <= inputs[i]) {
      const t = (v - inputs[i - 1]) / (inputs[i] - inputs[i - 1]);
      return outputs[i - 1] + t * (outputs[i] - outputs[i - 1]);
    }
  }
  return outputs[outputs.length - 1];
};

const railPositions = (count) => Array.from({ length: count }, (_, i) => i / (count - 1));

const ScrollThread = ({ targetRef, labels }) => {
  const shouldReduceMotion = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: targetRef, offset: ['start start', 'end end'] });
  const fractionsRef = useRef([]);
  const idleTimerRef = useRef(null);
  const [stopCount, setStopCount] = useState(0);
  const [hasRoom, setHasRoom] = useState(false);
  const [activeStop, setActiveStop] = useState(0);
  const [showLabel, setShowLabel] = useState(false);

  const toRail = (v) => {
    const f = fractionsRef.current;
    return interpolatePiecewise(f, railPositions(f.length), v);
  };
  const railTarget = useTransform(scrollYProgress, toRail);
  const railPos = useSpring(railTarget, SCROLL_THREAD.spring);
  const busTop = useTransform(railPos, (p) => `${p * 100}%`);

  useLayoutEffect(() => {
    const main = targetRef.current;
    if (!main) return undefined;

    const compute = () => {
      const container = main.querySelector('.container');
      const gutter = container
        ? container.getBoundingClientRect().left + parseFloat(getComputedStyle(container).paddingLeft || '0')
        : 0;
      setHasRoom(gutter >= SCROLL_THREAD.minGutterPx);

      const totalScrollable = main.scrollHeight - window.innerHeight;
      const children = Array.from(main.children);
      const n = children.length;
      if (totalScrollable <= 0 || n < 2) return;

      const reachOffset = window.innerHeight * SCROLL_THREAD.reachAt;
      const f = children.map((child) =>
        Math.min(1, Math.max(0, (child.offsetTop - main.offsetTop - reachOffset) / totalScrollable)),
      );
      // Strictement croissant, borné à [0, 1] : les sections de la dernière page d'écran se répartissent
      // sur la fin du scroll au lieu de toutes tomber à 100 %.
      f[0] = 0;
      for (let i = 1; i < n; i++) f[i] = Math.max(f[i], f[i - 1] + SCROLL_THREAD.minGap);
      for (let i = n - 1; i >= 0; i--) f[i] = Math.min(f[i], 1 - (n - 1 - i) * SCROLL_THREAD.minGap);
      fractionsRef.current = f;
      setStopCount(n);
      railPos.jump(toRail(scrollYProgress.get()));
    };

    compute();
    // Hauteurs qui bougent après coup (images, polices, contenus animés) : recalcul sans attendre un resize.
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(compute) : null;
    observer?.observe(main);
    window.addEventListener('resize', compute);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', compute);
    };
    // toRail ne lit que des refs ; railPos/scrollYProgress sont des motion values stables.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetRef]);

  // L'arrêt est « atteint » quand le bus (valeur amortie) y arrive, pas quand le scroll y passe :
  // l'onde d'arrivée coïncide avec le bus.
  useMotionValueEvent(railPos, 'change', (p) => {
    const n = fractionsRef.current.length;
    if (n < 2) return;
    const idx = Math.max(0, Math.min(n - 1, Math.floor(p * (n - 1) + 0.02)));
    setActiveStop((prev) => (prev === idx ? prev : idx));
  });

  useMotionValueEvent(scrollYProgress, 'change', () => {
    setShowLabel(true);
    clearTimeout(idleTimerRef.current);
    idleTimerRef.current = setTimeout(() => setShowLabel(false), SCROLL_THREAD.labelIdleMs);
  });
  useEffect(() => () => clearTimeout(idleTimerRef.current), []);

  if (shouldReduceMotion || !hasRoom || stopCount < 2) return null;

  const lastStop = stopCount - 1;
  const arrived = activeStop === lastStop;

  return (
    <div className="hidden lg:block fixed left-6 top-24 bottom-24 w-1 z-30 pointer-events-none" aria-hidden="true">
      <div className="absolute inset-0 bg-slate-200 rounded-full" />
      <motion.div
        className="absolute inset-0 bg-gradient-to-b from-blue-500 to-indigo-500 origin-top rounded-full"
        style={{ scaleY: railPos }}
      />

      {railPositions(stopCount).map((pos, i) => {
        if (i === lastStop) return null; // le terminus est l'école, dessinée sous le rail
        const reached = i <= activeStop;
        return (
          <div key={i} className="absolute left-1/2 w-0 h-0" style={{ top: `${pos * 100}%` }}>
            {i === activeStop && i > 0 && (
              <motion.span
                key={`ripple-${activeStop}`}
                className="absolute -left-3 -top-3 w-6 h-6 rounded-full border-2 border-blue-500"
                initial={{ scale: 0.5, opacity: 0.7 }}
                animate={{ scale: 1.7, opacity: 0 }}
                transition={{ duration: 0.7, ease: 'easeOut' }}
              />
            )}
            <motion.span
              className={cn(
                'absolute -left-1.5 -top-1.5 w-3 h-3 rounded-full border-2 transition-colors duration-300',
                reached ? 'bg-blue-600 border-blue-600' : 'bg-white border-slate-300',
              )}
              animate={{ scale: i === activeStop ? 1.3 : 1 }}
              transition={{ type: 'spring', stiffness: 400, damping: 20 }}
            />
          </div>
        );
      })}

      {/* Terminus : l'école, juste sous la fin du rail */}
      <div className="absolute left-1/2 w-0 h-0" style={{ top: 'calc(100% + 26px)' }}>
        {arrived && (
          <motion.span
            className="absolute -left-5 -top-5 w-10 h-10 rounded-xl border-2 border-green-500"
            initial={{ scale: 0.6, opacity: 0.7 }}
            animate={{ scale: 1.5, opacity: 0 }}
            transition={{ duration: 0.8, ease: 'easeOut' }}
          />
        )}
        <motion.div
          className={cn(
            'absolute -left-3.5 -top-3.5 w-7 h-7 rounded-lg bg-white border-2 shadow-sm flex items-center justify-center transition-colors duration-300',
            arrived ? 'border-green-500' : 'border-indigo-200',
          )}
          animate={{ scale: arrived ? 1.15 : 1 }}
          transition={{ type: 'spring', stiffness: 400, damping: 18 }}
        >
          <School className="w-4 h-4 text-indigo-600" />
        </motion.div>
      </div>

      <motion.div
        className="absolute -left-[10px] w-6 h-6 rounded-full bg-blue-600 shadow-lg shadow-blue-400/50 ring-4 ring-blue-100 flex items-center justify-center"
        style={{ top: busTop, marginTop: '-12px' }}
      >
        <Bus className="w-3.5 h-3.5 text-white" />
      </motion.div>

      {/* Libellé de l'arrêt courant — visible pendant le scroll uniquement */}
      <AnimatePresence>
        {showLabel && (
          <motion.div
            className="absolute left-6"
            style={{ top: busTop }}
            initial={{ opacity: 0, x: -6 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -6 }}
            transition={{ duration: 0.2 }}
          >
            <div className="-translate-y-1/2 whitespace-nowrap rounded-full bg-white/95 backdrop-blur px-3 py-1 text-xs font-semibold text-slate-700 shadow-md border border-slate-100">
              <AnimatePresence mode="wait" initial={false}>
                <motion.span
                  key={activeStop}
                  className="inline-block"
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -4 }}
                  transition={{ duration: 0.15 }}
                >
                  <span className="text-blue-600 tabular-nums">
                    {activeStop + 1}/{stopCount}
                  </span>{' '}
                  · {labels[activeStop] ?? ''}
                </motion.span>
              </AnimatePresence>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// --- Navbar ---
const Navbar = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const navLinks = [
    { href: '#how-it-works', label: 'Comment ça marche' },
    { href: '#features', label: 'Fonctionnalités' },
    { href: '#pricing', label: 'Tarifs' },
    { href: '#contact', label: 'Contact' },
  ];

  return (
    <nav className="fixed top-0 w-full z-50 bg-white/90 backdrop-blur-md border-b border-slate-100">
      <div className="container mx-auto px-4 h-20 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <img src="/logo.png" alt="M'EyeBus Logo" className="h-12 w-auto" />
          <img src="/logo_trademark.png" alt="M'EyeBus" className="h-6 w-auto object-contain" />
        </div>

        <div className="hidden md:flex items-center gap-8">
          {navLinks.map((l) => (
            <a
              key={l.href}
              href={l.href}
              className="text-sm font-medium text-slate-600 hover:text-blue-600 transition-colors"
            >
              {l.label}
            </a>
          ))}
        </div>

        <div className="hidden md:flex items-center gap-4">
          <button
            onClick={() => navigate('/login')}
            className="text-sm font-medium text-slate-600 hover:text-blue-600 transition-colors"
          >
            Se connecter
          </button>
          <div className="flex items-center gap-2">
            <SocialLinks itemClassName="h-10 w-10 rounded-xl border-2" />
            <WhatsAppButton iconOnly variant="outline" className="!p-2" />
          </div>
          <Button
            onClick={scrollToContact}
            className="bg-blue-600 text-white hover:bg-blue-700 rounded-lg px-5 py-2.5 text-sm font-medium"
          >
            Réserver une démo
          </Button>
        </div>

        <div className="flex items-center gap-2 md:hidden">
          {/* < sm : pas la place à côté du logo sur 360 px → icônes dans le menu déroulant */}
          <SocialLinks className="hidden sm:flex" itemClassName="h-10 w-10 rounded-xl border-2" />
          <WhatsAppButton iconOnly variant="outline" className="!p-2" />
          <button
            className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
            onClick={() => setOpen(!open)}
            aria-label="Menu"
          >
            {open ? <X className="h-6 w-6 text-slate-700" /> : <Menu className="h-6 w-6 text-slate-700" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="md:hidden bg-white border-t border-slate-100 overflow-hidden"
          >
            <div className="container mx-auto px-4 py-4 flex flex-col gap-1">
              {navLinks.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  className="text-sm font-medium text-slate-700 py-3 px-2 rounded-lg hover:bg-slate-50 transition-colors"
                >
                  {l.label}
                </a>
              ))}
              <hr className="border-slate-100 my-2" />
              <button
                onClick={() => {
                  navigate('/login');
                  setOpen(false);
                }}
                className="text-sm font-medium text-slate-600 py-3 px-2 text-left rounded-lg hover:bg-slate-50"
              >
                Se connecter
              </button>
              <Button
                onClick={() => {
                  scrollToContact();
                  setOpen(false);
                }}
                className="bg-blue-600 text-white w-full mt-2 py-3 rounded-xl"
              >
                Réserver une démo
              </Button>
              <SocialLinks className="sm:hidden justify-center mt-3" itemClassName="h-10 w-10 rounded-full border" />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

// --- Hero : animation du trajet ---
// Boucle scénarisée pilotée par une seule valeur `progress` (0..1 de la longueur du tracé) : le bus
// accélère, freine et marque l'arrêt à chaque point de ramassage, puis arrive à l'école. Le tracé
// parcouru, la vitesse affichée et la notification lisent ce même état — ils racontent ce que voit le parent.
const HERO_ROUTE_PATH = 'M 50 200 C 90 200 95 110 150 110 S 215 190 265 175 S 320 110 345 110';
const HERO_ROUTE_START = { x: 50, y: 200 };
const HERO_STOPS = [0.3, 0.64]; // arrêts, en fraction de la longueur du tracé (l'école est à 1)
const HERO_TIMING = {
  routeS: 7, // durée de conduite pour le tracé complet (hors arrêts) — vitesse identique sur chaque tronçon
  dwellMs: 1200, // temps passé à chaque arrêt
  arrivedMs: 2400, // pause à l'école avant de relancer la boucle
  fadeS: 0.45,
  introDelayMs: 1500, // laisse le tracé se dessiner avant le premier départ
};
const HERO_DRIVE_EASE = [0.45, 0, 0.2, 1]; // démarrage progressif, freinage doux
const HERO_MAX_KMH = 48;
const HERO_PEAK_VELOCITY = 0.26; // vitesse (fraction de tracé / s) affichée à HERO_MAX_KMH
const HERO_BUS = {
  wheelR: 4, // rayon des roues (unités SVG) — rotation calée sur la distance parcourue
  tiltFactor: 0.5, // part de la pente du tracé reprise par la caisse
  maxTiltDeg: 25,
  tangentSample: 0.004, // écart (fraction de tracé) pour estimer la direction
  bobPeriod: 5, // unités SVG parcourues par oscillation de suspension
  bobAmp: 0.6,
};

const HERO_TONES = {
  blue: { badge: 'bg-blue-100 text-blue-600', bar: 'bg-blue-500' },
  orange: { badge: 'bg-orange-100 text-orange-600', bar: 'bg-orange-500' },
  green: { badge: 'bg-green-100 text-green-600', bar: 'bg-green-500' },
};

const heroCardContent = (phase) => {
  if (phase.type === 'arrived') {
    return { tone: 'green', Icon: Bell, title: "Bus arrivé à l'école", detail: "À l'instant" };
  }
  if (phase.type === 'stop') {
    return { tone: 'orange', Icon: MapPin, title: `Bus à l'arrêt ${phase.index + 1}`, detail: 'Ramassage en cours' };
  }
  if (phase.index >= HERO_STOPS.length) {
    return { tone: 'blue', Icon: Navigation, title: "En route vers l'école", detail: 'Suivi en direct' };
  }
  return {
    tone: 'blue',
    Icon: Navigation,
    title: `En route vers l'arrêt ${phase.index + 1}`,
    detail: 'Suivi en direct',
  };
};

const HeroTripAnimation = () => {
  const shouldReduceMotion = useReducedMotion();
  const pathRef = useRef(null);
  const lengthRef = useRef(0);
  const [stopPoints, setStopPoints] = useState([]);
  const [endPoint, setEndPoint] = useState(null);
  const [phase, setPhase] = useState({ type: 'drive', index: 0 });
  const [lap, setLap] = useState(0);

  const progress = useMotionValue(0);
  const tripOpacity = useMotionValue(1);
  const pointAt = (p) => {
    const path = pathRef.current;
    if (!path || !lengthRef.current) return HERO_ROUTE_START;
    return path.getPointAtLength(p * lengthRef.current);
  };
  const busPoint = useTransform(progress, pointAt);
  const busX = useTransform(busPoint, (pt) => pt.x);
  const busY = useTransform(busPoint, (pt) => pt.y);
  // La caisse s'incline dans la direction du tracé (amortie), les roues tournent selon la distance parcourue,
  // la suspension oscille uniquement quand le bus roule.
  const busAngle = useTransform(progress, (p) => {
    const a = pointAt(Math.max(0, p - HERO_BUS.tangentSample));
    const b = pointAt(Math.min(1, p + HERO_BUS.tangentSample));
    const deg = (Math.atan2(b.y - a.y, b.x - a.x) * 180) / Math.PI;
    return Math.max(-HERO_BUS.maxTiltDeg, Math.min(HERO_BUS.maxTiltDeg, deg * HERO_BUS.tiltFactor));
  });
  const busTilt = useSpring(busAngle, { stiffness: 120, damping: 20 });
  const wheelRotate = useTransform(progress, (p) => ((p * lengthRef.current) / HERO_BUS.wheelR) * (180 / Math.PI));
  const busBob = useTransform(
    progress,
    (p) => Math.sin((p * lengthRef.current) / HERO_BUS.bobPeriod) * HERO_BUS.bobAmp,
  );
  const speedKmh = useTransform(useVelocity(progress), (v) =>
    Math.round(Math.min(HERO_MAX_KMH, (Math.abs(v) / HERO_PEAK_VELOCITY) * HERO_MAX_KMH)),
  );

  // Géométrie réelle du tracé : arrêts et école posés exactement sur la courbe.
  useLayoutEffect(() => {
    const path = pathRef.current;
    if (!path || typeof path.getTotalLength !== 'function') return;
    const length = path.getTotalLength();
    lengthRef.current = length;
    setStopPoints(HERO_STOPS.map((f) => path.getPointAtLength(f * length)));
    setEndPoint(path.getPointAtLength(length));
  }, []);

  useEffect(() => {
    if (!endPoint) return undefined;
    if (shouldReduceMotion) {
      // Image fixe représentative : bus au 2e arrêt, tracé parcouru jusque-là.
      progress.jump(HERO_STOPS[1]);
      setPhase({ type: 'stop', index: 1 });
      return undefined;
    }

    let cancelled = false;
    const controls = [];
    const timeouts = [];
    const play = (value, to, options) => {
      const c = animate(value, to, options);
      controls.push(c);
      return c;
    };
    const wait = (ms) => new Promise((resolve) => timeouts.push(setTimeout(resolve, ms)));

    const run = async () => {
      await wait(HERO_TIMING.introDelayMs);
      while (!cancelled) {
        let from = 0;
        const targets = [...HERO_STOPS, 1];
        for (let i = 0; i < targets.length; i++) {
          setPhase({ type: 'drive', index: i });
          await play(progress, targets[i], {
            duration: (targets[i] - from) * HERO_TIMING.routeS,
            ease: HERO_DRIVE_EASE,
          });
          if (cancelled) return;
          from = targets[i];
          if (i < HERO_STOPS.length) {
            setPhase({ type: 'stop', index: i });
            await wait(HERO_TIMING.dwellMs);
            if (cancelled) return;
          }
        }
        setPhase({ type: 'arrived' });
        await wait(HERO_TIMING.arrivedMs);
        if (cancelled) return;

        // Fin de boucle : le trajet s'efface puis repart du départ, sans téléportation visible.
        await play(tripOpacity, 0, { duration: HERO_TIMING.fadeS });
        if (cancelled) return;
        progress.jump(0);
        setPhase({ type: 'drive', index: 0 });
        setLap((n) => n + 1);
        await play(tripOpacity, 1, { duration: HERO_TIMING.fadeS });
      }
    };
    run();

    return () => {
      cancelled = true;
      controls.forEach((c) => c.stop());
      timeouts.forEach(clearTimeout);
    };
  }, [endPoint, shouldReduceMotion, progress, tripOpacity]);

  const stopsDone =
    phase.type === 'arrived' ? HERO_STOPS.length : phase.type === 'stop' ? phase.index + 1 : phase.index;
  const card = heroCardContent(phase);
  const tone = HERO_TONES[card.tone];
  const cardKey = phase.type === 'arrived' ? 'arrived' : `${phase.type}-${phase.index}`;

  return (
    <div className="absolute inset-0 flex items-center justify-center">
      <svg className="absolute w-full h-full" viewBox="0 0 400 300" preserveAspectRatio="xMidYMid meet">
        <defs>
          <filter id="heroShadow" x="-50%" y="-50%" width="200%" height="200%">
            <feDropShadow dx="0" dy="2" stdDeviation="3" floodColor="#1E3A8A" floodOpacity="0.25" />
          </filter>
          <pattern id="heroDotGrid" width="22" height="22" patternUnits="userSpaceOnUse">
            <circle cx="2" cy="2" r="1.3" fill="#94A3B8" opacity="0.35" />
          </pattern>
        </defs>

        <rect x="0" y="0" width="400" height="300" fill="url(#heroDotGrid)" />

        {/* Tracé de mesure (invisible) : source unique de la géométrie. */}
        <path ref={pathRef} d={HERO_ROUTE_PATH} fill="none" stroke="none" />

        {/* Itinéraire complet (reste à parcourir) */}
        <motion.path
          d={HERO_ROUTE_PATH}
          fill="none"
          stroke="#BFDBFE"
          strokeWidth="6"
          strokeLinecap="round"
          initial={shouldReduceMotion ? { pathLength: 1 } : { pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 1.4, delay: 0.5, ease: 'easeInOut' }}
        />

        {/* Portion parcourue, qui suit le bus */}
        <motion.path
          d={HERO_ROUTE_PATH}
          fill="none"
          stroke="#2563EB"
          strokeWidth="6"
          strokeLinecap="round"
          style={{ pathLength: progress, opacity: tripOpacity }}
        />

        {/* Départ neutre */}
        <circle cx={HERO_ROUTE_START.x} cy={HERO_ROUTE_START.y} r="4" fill="#94A3B8" />

        {/* Arrêts : se remplissent au passage du bus, onde au moment de l'arrêt */}
        {stopPoints.map((pt, i) => {
          const done = i < stopsDone;
          return (
            <g key={i}>
              {!shouldReduceMotion && phase.type === 'stop' && phase.index === i && (
                <motion.circle
                  key={`ripple-${lap}`}
                  cx={pt.x}
                  cy={pt.y}
                  fill="none"
                  stroke="#2563EB"
                  strokeWidth="2"
                  initial={{ r: 7, opacity: 0.6 }}
                  animate={{ r: 20, opacity: 0 }}
                  transition={{ duration: 0.9, ease: 'easeOut' }}
                />
              )}
              <motion.circle
                cx={pt.x}
                cy={pt.y}
                r="7"
                strokeWidth="2.5"
                initial={shouldReduceMotion ? false : { scale: 0 }}
                animate={{ scale: 1, fill: done ? '#2563EB' : '#FFFFFF', stroke: done ? '#2563EB' : '#93C5FD' }}
                transition={{ scale: { delay: 0.8 + i * 0.25, type: 'spring' }, default: { duration: 0.25 } }}
                style={{ transformOrigin: `${pt.x}px ${pt.y}px` }}
              />
              <motion.circle
                cx={pt.x}
                cy={pt.y}
                r="2.5"
                fill="#FFFFFF"
                initial={false}
                animate={{ opacity: done ? 1 : 0 }}
              />
            </g>
          );
        })}

        {/* École, à l'extrémité exacte du tracé */}
        {endPoint && (
          <g transform={`translate(${endPoint.x}, ${endPoint.y - 34})`} filter="url(#heroShadow)">
            <motion.rect
              x="-16"
              y="-16"
              width="32"
              height="32"
              rx="9"
              fill="white"
              strokeWidth="1.5"
              initial={false}
              animate={{ stroke: phase.type === 'arrived' ? '#22C55E' : '#C7D2FE' }}
            />
            <School x="-9" y="-9" width="18" height="18" strokeWidth={2.2} className="text-indigo-600" />
            <line x1="0" y1="16" x2="0" y2="27" stroke="#C7D2FE" strokeWidth="1.5" strokeDasharray="2 2" />
          </g>
        )}

        {/* Bus */}
        <motion.g style={{ x: busX, y: busY, opacity: tripOpacity }}>
          {!shouldReduceMotion && (
            <circle cy="-6" r="18" fill="#3B82F6" opacity="0.2">
              <animate attributeName="r" values="16;22;16" dur="2.5s" repeatCount="indefinite" />
              <animate attributeName="opacity" values="0.25;0.04;0.25" dur="2.5s" repeatCount="indefinite" />
            </circle>
          )}
          {/* Bus de profil (vers la droite), roues posées sur le tracé */}
          <motion.g style={{ rotate: busTilt, originX: 0.5, originY: 1 }}>
            <ellipse cx="0" cy="6.5" rx="17" ry="2.2" fill="#1E3A8A" opacity="0.18" />
            <motion.g style={{ y: busBob }} filter="url(#heroShadow)">
              {/* Caisse : arrière droit, avant arrondi */}
              <path
                d="M -19 -17 Q -19 -20 -16 -20 L 12 -20 Q 16 -20 18 -16 L 20 -9 Q 20.5 -7 20.5 -5 L 20.5 1 Q 20.5 3 18.5 3 L -17 3 Q -19 3 -19 1 Z"
                fill="#2563EB"
                stroke="white"
                strokeWidth="1.4"
                strokeLinejoin="round"
              />
              <rect x="-16" y="-22" width="26" height="2.5" rx="1.25" fill="#1D4ED8" />
              {/* Vitres latérales + pare-brise */}
              <rect x="-16" y="-16" width="7" height="7" rx="1.5" fill="#DBEAFE" />
              <rect x="-7.5" y="-16" width="7" height="7" rx="1.5" fill="#DBEAFE" />
              <rect x="1" y="-16" width="7" height="7" rx="1.5" fill="#DBEAFE" />
              <path d="M 10.5 -16 L 15 -16 Q 16.5 -16 17.2 -14.5 L 19 -9 L 10.5 -9 Z" fill="#BFDBFE" />
              <rect x="-15" y="-15" width="2" height="5" rx="1" fill="white" opacity="0.6" />
              {/* Bande orange (même accent que le Live ETA) + porte */}
              <rect x="-19" y="-6" width="39.5" height="2.2" fill="#F97316" />
              <rect x="11" y="-8.5" width="0.9" height="10" fill="#1D4ED8" opacity="0.5" />
              {/* Phare avant, feu arrière */}
              <rect x="18.8" y="-2.5" width="1.9" height="2.2" rx="0.8" fill="#FDE68A" />
              <rect x="-19.4" y="-2.5" width="1.4" height="2.2" rx="0.6" fill="#F87171" />
            </motion.g>
            {[-10, 11].map((cx) => (
              <g key={cx} transform={`translate(${cx}, 3)`}>
                <circle r={HERO_BUS.wheelR + 0.8} fill="white" />
                <motion.g style={{ rotate: wheelRotate }}>
                  <circle r={HERO_BUS.wheelR} fill="#0F172A" />
                  <circle r="1.7" fill="#CBD5E1" />
                  <rect x="-0.45" y={-HERO_BUS.wheelR + 0.6} width="0.9" height="2" rx="0.45" fill="#CBD5E1" />
                </motion.g>
              </g>
            ))}
          </motion.g>
        </motion.g>
      </svg>

      {/* Notification : suit l'état du trajet */}
      <motion.div
        animate={shouldReduceMotion ? {} : { y: [0, -8, 0] }}
        transition={shouldReduceMotion ? {} : { repeat: Infinity, duration: 4 }}
        className="absolute bottom-8 right-8 bg-white p-4 rounded-xl shadow-lg border border-slate-100 z-20 w-52 hidden sm:block"
        aria-live="off"
      >
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={cardKey}
            initial={shouldReduceMotion ? false : { opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={shouldReduceMotion ? {} : { opacity: 0, y: -6 }}
            transition={{ duration: 0.2 }}
            className="flex items-center gap-3 mb-2"
          >
            <div className={cn('p-2 rounded-lg transition-colors', tone.badge)}>
              <card.Icon className="h-4 w-4" />
            </div>
            <div className="min-w-0">
              <p className="text-xs font-bold text-slate-800 truncate">{card.title}</p>
              <p className="text-xs text-slate-500">{card.detail}</p>
            </div>
          </motion.div>
        </AnimatePresence>
        <div className="h-1 w-full bg-slate-100 rounded-full overflow-hidden">
          <motion.div
            className={cn('h-full w-full rounded-full origin-left transition-colors', tone.bar)}
            style={{ scaleX: progress }}
          />
        </div>
      </motion.div>

      {/* Vitesse, dérivée du mouvement réel du bus */}
      <motion.div
        animate={shouldReduceMotion ? {} : { y: [0, 8, 0] }}
        transition={shouldReduceMotion ? {} : { repeat: Infinity, duration: 5, delay: 1 }}
        className="absolute top-8 left-8 bg-white px-4 py-2 rounded-full shadow-lg border border-slate-100 z-20 hidden sm:block"
      >
        <p className="text-xs font-bold text-slate-700 flex items-center gap-2 tabular-nums">
          <span className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" />
          Bus #42 · <motion.span>{speedKmh}</motion.span> km/h
        </p>
      </motion.div>
    </div>
  );
};

// --- Hero ---
const HeroSection = () => {
  return (
    <section className="relative pt-32 pb-20 lg:pt-48 lg:pb-32 overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-white to-blue-50 pointer-events-none" />
      <div className="absolute top-20 right-0 w-[600px] h-[600px] bg-gradient-to-br from-blue-100 to-indigo-100 rounded-full blur-3xl opacity-40 pointer-events-none" />

      <div className="container mx-auto px-4 relative z-10">
        <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20">
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7 }}
            className="lg:w-1/2 text-center lg:text-left"
          >
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-50 border border-blue-100 text-blue-600 text-sm font-semibold mb-6">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-500" />
              </span>
              TRANSPORT SCOLAIRE CONNECTÉ
            </div>

            <h1 className="text-5xl lg:text-6xl xl:text-7xl font-bold text-slate-900 leading-tight mb-6 tracking-tight">
              Le transport scolaire,{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">
                enfin sécurisé.
              </span>
            </h1>

            <p className="text-xl text-slate-600 mb-8 leading-relaxed max-w-xl mx-auto lg:mx-0">
              M'EyeBus connecte écoles, parents et chauffeurs en temps réel. Plus de stress, plus d'appels, juste de la
              sérénité.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
              <Button
                size="lg"
                onClick={scrollToContact}
                className="!h-auto bg-blue-600 hover:bg-blue-700 text-white px-8 py-6 text-lg rounded-xl shadow-lg shadow-blue-200 transition-all hover:scale-105"
              >
                Demander une démo <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={() => document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' })}
                className="!h-auto px-8 py-6 text-lg rounded-xl border-2"
              >
                Comment ça marche
              </Button>
            </div>

            <div className="mt-10 flex items-center justify-center lg:justify-start gap-6 text-sm text-slate-500 font-medium flex-wrap">
              <span className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-blue-500" /> Installation en 5 min
              </span>
              <span className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-blue-500" /> Support 24/7 inclus
              </span>
              <span className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-blue-500" /> Sans engagement
              </span>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="lg:w-1/2 relative"
          >
            <div className="relative bg-white rounded-3xl shadow-2xl border border-slate-100 p-2 overflow-hidden transform rotate-1 hover:rotate-0 transition-transform duration-500">
              <div className="bg-gradient-to-br from-slate-50 to-blue-50 rounded-2xl overflow-hidden aspect-[4/3] relative">
                <div className="absolute -top-12 -left-10 w-44 h-44 bg-blue-200/40 rounded-full blur-3xl pointer-events-none" />
                <div className="absolute -bottom-16 -right-8 w-56 h-56 bg-indigo-200/30 rounded-full blur-3xl pointer-events-none" />
                <HeroTripAnimation />
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// --- Stats Bar ---
const StatsBar = () => {
  const stats = [
    { value: '-70%', label: "d'appels parents" },
    { value: '100%', label: 'traçabilité GPS' },
    { value: '-15%', label: 'de retards constatés' },
  ];

  return (
    <section className="py-14 bg-slate-900">
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 text-center">
          {stats.map((s, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
            >
              <div className="text-3xl md:text-4xl font-bold text-blue-400 mb-1">{s.value}</div>
              <div className="text-sm text-slate-400 font-medium">{s.label}</div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// --- Problème ---
const ProblemSection = () => {
  const shouldReduceMotion = useReducedMotion();

  return (
    <section className="py-24 bg-white">
      <div className="container mx-auto px-4">
        <div className="flex flex-col lg:flex-row items-center gap-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.6 }}
            className="lg:w-1/2 flex justify-center"
          >
            <div
              className="relative w-52 bg-white rounded-[2.5rem] p-3 shadow-xl border border-slate-200"
              role="img"
              aria-label="Écran de téléphone affichant une carte vide, sans position du bus"
            >
              <div className="bg-slate-50 rounded-[2rem] aspect-[9/16] relative overflow-hidden flex items-center justify-center">
                <svg className="absolute inset-0 w-full h-full" viewBox="0 0 200 350" aria-hidden="true">
                  <defs>
                    <pattern id="problemGrid" width="20" height="20" patternUnits="userSpaceOnUse">
                      <path d="M 20 0 L 0 0 0 20" fill="none" stroke="#E2E8F0" strokeWidth="1" />
                    </pattern>
                  </defs>
                  <rect width="200" height="350" fill="url(#problemGrid)" />
                </svg>

                <div className="relative z-10 flex flex-col items-center gap-3">
                  <motion.div
                    animate={shouldReduceMotion ? {} : { scale: [1, 1.08, 1] }}
                    transition={shouldReduceMotion ? {} : { repeat: Infinity, duration: 2 }}
                    className="w-14 h-14 rounded-full bg-white border-2 border-slate-200 shadow-sm flex items-center justify-center"
                  >
                    <span className="text-2xl font-bold text-slate-300">?</span>
                  </motion.div>
                  <p className="text-xs font-medium text-slate-400">Recherche du bus…</p>
                </div>
              </div>
            </div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="lg:w-1/2 text-center lg:text-left"
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-6">« Où est le bus ? »</h2>
            <p className="text-lg text-slate-600 leading-relaxed max-w-xl mx-auto lg:mx-0">
              Les parents ne devraient pas avoir à appeler l&apos;école pour savoir où se trouve le bus. Sans
              visibilité, chaque trajet devient une source d&apos;attente et d&apos;incertitude — pour les familles
              comme pour l&apos;école.
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// --- How It Works ---
const HowItWorksSection = () => {
  const steps = [
    {
      icon: <Bus className="h-7 w-7 text-blue-600" />,
      color: 'blue',
      title: 'Installation en 5 min',
      desc: 'Le boîtier GPS se connecte sur le bus. Aucun câblage complexe, aucun technicien requis.',
    },
    {
      icon: <Navigation className="h-7 w-7 text-indigo-600" />,
      color: 'indigo',
      title: 'Suivi en temps réel',
      desc: "L'école pilote sa flotte depuis le dashboard admin. Les parents suivent le bus sur l'app mobile.",
    },
    {
      icon: <Bell className="h-7 w-7 text-orange-500" />,
      color: 'orange',
      title: 'Alertes automatiques',
      desc: "Les parents reçoivent des notifications push au départ, à l'approche et à l'arrivée. Zéro appel.",
    },
  ];

  const colorMap = {
    blue: 'bg-blue-50 border-blue-100',
    indigo: 'bg-indigo-50 border-indigo-100',
    orange: 'bg-orange-50 border-orange-100',
  };

  return (
    <section className="py-24 bg-white" id="how-it-works">
      <div className="container mx-auto px-4">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <div className="inline-block px-3 py-1 bg-blue-50 text-blue-600 rounded-full text-sm font-semibold mb-4">
            COMMENT ÇA MARCHE
          </div>
          <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">Opérationnel en une journée.</h2>
          <p className="text-lg text-slate-600">
            De l'installation à la première notification parent, en moins de 24h.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative">
          <div className="hidden md:block absolute top-10 left-[calc(16.5%+40px)] right-[calc(16.5%+40px)] h-px bg-gradient-to-r from-blue-200 via-indigo-200 to-orange-200" />

          {steps.map((step, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.15 }}
              className="flex flex-col items-center text-center"
            >
              <div className="relative mb-6">
                <div
                  className={`w-20 h-20 ${colorMap[step.color]} border-2 rounded-2xl flex items-center justify-center`}
                >
                  {step.icon}
                </div>
                <span className="absolute -top-3 -right-3 w-7 h-7 bg-slate-900 text-white rounded-full text-xs font-bold flex items-center justify-center shadow-lg">
                  {i + 1}
                </span>
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">{step.title}</h3>
              <p className="text-slate-600 leading-relaxed max-w-xs">{step.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// --- Hub de connexions (solution) ---
const SolutionHub = () => {
  const nodes = [
    { icon: <Users className="h-5 w-5" />, label: 'Parents', angle: -90 },
    { icon: <Bus className="h-5 w-5" />, label: 'Bus', angle: -30 },
    { icon: <UserCheck className="h-5 w-5" />, label: 'Chauffeur', angle: 30 },
    { icon: <LayoutDashboard className="h-5 w-5" />, label: 'École', angle: 90 },
    { icon: <Smartphone className="h-5 w-5" />, label: 'Application', angle: 150 },
    { icon: <Zap className="h-5 w-5" />, label: 'Dashboard', angle: 210 },
  ];
  const radius = 110;
  const center = 140;

  return (
    <div className="relative mx-auto mb-12 w-[280px] h-[280px]">
      <svg viewBox="0 0 280 280" className="absolute inset-0 w-full h-full" aria-hidden="true">
        {nodes.map((n, i) => {
          const rad = (n.angle * Math.PI) / 180;
          const x = center + radius * Math.cos(rad);
          const y = center + radius * Math.sin(rad);
          return (
            <motion.line
              key={n.label}
              x1={center}
              y1={center}
              x2={x}
              y2={y}
              stroke="#C7D2FE"
              strokeWidth="2"
              initial={{ pathLength: 0, opacity: 0 }}
              whileInView={{ pathLength: 1, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.15 }}
            />
          );
        })}
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-blue-600 to-indigo-600 shadow-lg shadow-blue-200 flex items-center justify-center text-white font-bold text-xs text-center px-2 leading-tight">
          MeyeBus
        </div>
      </div>
      {nodes.map((n, i) => {
        const rad = (n.angle * Math.PI) / 180;
        const x = center + radius * Math.cos(rad);
        const y = center + radius * Math.sin(rad);
        return (
          <motion.div
            key={n.label}
            initial={{ opacity: 0, scale: 0.6 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.15 + 0.4, duration: 0.3 }}
            className="absolute w-14 h-14 -ml-7 -mt-7 rounded-xl bg-white shadow-md border border-slate-100 flex items-center justify-center text-blue-600"
            style={{ left: x, top: y }}
            title={n.label}
          >
            {n.icon}
          </motion.div>
        );
      })}
    </div>
  );
};

// --- Features (personas) ---
const FeaturesSection = () => (
  <section className="py-24 bg-slate-50" id="features">
    <div className="container mx-auto px-4">
      <div className="text-center max-w-3xl mx-auto mb-4">
        <div className="inline-block px-3 py-1 bg-indigo-50 text-indigo-600 rounded-full text-sm font-semibold mb-4">
          LA SOLUTION
        </div>
        <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">Tout le transport scolaire, connecté.</h2>
      </div>

      <SolutionHub />

      <div className="text-center max-w-3xl mx-auto mb-16">
        <p className="text-lg text-slate-600">
          Une seule plateforme relie parents, bus, chauffeurs et école en temps réel.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
        {/* Écoles */}
        <motion.div
          whileHover={{ y: -6 }}
          className="bg-white p-8 rounded-3xl shadow-sm border border-slate-100 relative overflow-hidden group flex flex-col"
        >
          <div className="absolute top-0 right-0 w-32 h-32 bg-blue-50 rounded-bl-full -mr-8 -mt-8 transition-transform group-hover:scale-110" />
          <div className="relative z-10 flex flex-col flex-grow">
            <div className="w-12 h-12 bg-blue-600 rounded-2xl flex items-center justify-center mb-6 shadow-lg shadow-blue-200">
              <LayoutDashboard className="h-6 w-6 text-white" />
            </div>
            <h3 className="text-2xl font-bold text-slate-900 mb-3">Pour les Écoles</h3>
            <p className="text-slate-600 mb-8 flex-grow">
              Pilotez votre flotte avec une précision chirurgicale. Tableaux de bord, rapports automatisés et vue
              globale en temps réel.
            </p>
            <ul className="space-y-3 mt-auto">
              {['Vue Flotte 360°', 'Analytics & rapports', 'Gestion circuits'].map((f) => (
                <li key={f} className="flex items-center gap-3 text-sm font-medium text-slate-700">
                  <span className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-100 text-blue-600 shrink-0">
                    <CheckCircle2 className="w-4 h-4" />
                  </span>
                  {f}
                </li>
              ))}
            </ul>
          </div>
        </motion.div>

        {/* Parents */}
        <motion.div
          whileHover={{ y: -6 }}
          className="bg-slate-900 p-8 rounded-3xl shadow-xl text-white relative overflow-hidden flex flex-col"
        >
          <div className="absolute bottom-0 right-0 w-40 h-40 bg-blue-600 rounded-tl-full opacity-20" />
          <div className="relative z-10 flex flex-col flex-grow">
            <div className="w-12 h-12 bg-orange-500 rounded-2xl flex items-center justify-center mb-6 shadow-lg shadow-orange-900/50">
              <ShieldCheck className="h-6 w-6 text-white" />
            </div>
            <h3 className="text-2xl font-bold mb-3">Pour les Parents</h3>
            <p className="text-slate-300 mb-8 flex-grow">
              La tranquillité d'esprit absolue. Suivez le bus et recevez des alertes instantanées sur votre téléphone.
            </p>
            <ul className="space-y-3 mt-auto">
              {['Tracking live', "Notifs 'Bus approche'", 'Confirmation montée'].map((f) => (
                <li key={f} className="flex items-center gap-3 text-sm font-medium text-slate-200">
                  <span className="flex items-center justify-center w-6 h-6 rounded-full bg-orange-500/20 text-orange-400 shrink-0">
                    <CheckCircle2 className="w-4 h-4" />
                  </span>
                  {f}
                </li>
              ))}
            </ul>
          </div>
        </motion.div>

        {/* Chauffeurs */}
        <motion.div
          whileHover={{ y: -6 }}
          className="bg-white p-8 rounded-3xl shadow-sm border border-slate-100 relative overflow-hidden group flex flex-col"
        >
          <div className="absolute top-0 left-0 w-24 h-24 bg-green-50 rounded-br-full -ml-4 -mt-4 transition-transform group-hover:scale-110" />
          <div className="relative z-10 flex flex-col flex-grow">
            <div className="w-12 h-12 bg-green-500 rounded-2xl flex items-center justify-center mb-6 shadow-lg shadow-green-200">
              <UserCheck className="h-6 w-6 text-white" />
            </div>
            <h3 className="text-2xl font-bold text-slate-900 mb-3">Pour les Chauffeurs</h3>
            <p className="text-slate-600 mb-8 flex-grow">
              Une app simple et intuitive pour se concentrer sur la route. Pointage élèves, gestion arrêts, signalement
              incident.
            </p>
            <ul className="space-y-3 mt-auto">
              {['Pointage montée/descente', 'Liste élèves par arrêt', 'Navigation GPS'].map((f) => (
                <li key={f} className="flex items-center gap-3 text-sm font-medium text-slate-700">
                  <span className="flex items-center justify-center w-6 h-6 rounded-full bg-green-100 text-green-600 shrink-0">
                    <CheckCircle2 className="w-4 h-4" />
                  </span>
                  {f}
                </li>
              ))}
            </ul>
          </div>
        </motion.div>
      </div>

      {/* Impact banner */}
      <motion.div
        whileHover={{ scale: 1.005 }}
        className="bg-gradient-to-r from-blue-600 to-indigo-600 p-8 md:p-12 rounded-3xl shadow-xl text-white flex flex-col lg:flex-row items-center justify-between gap-8"
      >
        <div className="lg:w-1/3">
          <h3 className="text-3xl font-bold mb-2">L'impact M'EyeBus</h3>
          <p className="text-blue-100 text-lg">Des résultats concrets dès le premier mois.</p>
        </div>
        <div className="lg:w-2/3 flex flex-wrap justify-center lg:justify-end gap-8 md:gap-16 text-center">
          {[
            { value: 70, prefix: '-', suffix: '%', lbl: 'Appels Parents' },
            { value: 100, suffix: '%', lbl: 'Traçabilité' },
            { value: 15, prefix: '-', suffix: '%', lbl: 'Retards' },
          ].map((s) => (
            <div key={s.lbl}>
              <div className="text-4xl md:text-5xl font-bold mb-1">
                <Counter value={s.value} prefix={s.prefix} suffix={s.suffix} />
              </div>
              <div className="text-sm text-blue-200 font-medium uppercase tracking-wider">{s.lbl}</div>
            </div>
          ))}
        </div>
      </motion.div>
    </div>
  </section>
);

// --- Expérience Parent ---
const ParentExperienceSection = () => {
  const notifications = [
    { icon: <Bus className="h-4 w-4 text-blue-400" />, title: 'Votre bus vient de partir', time: '07:42' },
    { icon: <MapPin className="h-4 w-4 text-orange-400" />, title: 'Votre bus est à 800 m', time: '07:56' },
    { icon: <Clock className="h-4 w-4 text-indigo-400" />, title: 'Arrivée prévue dans 3 min', time: '07:58' },
    { icon: <CheckCircle2 className="h-4 w-4 text-green-400" />, title: "Bus arrivé à l'école", time: '08:01' },
  ];

  return (
    <section className="py-24 bg-white overflow-hidden">
      <div className="container mx-auto px-4">
        <div className="flex flex-col lg:flex-row-reverse items-center gap-16">
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="lg:w-1/2"
          >
            <div className="relative mx-auto max-w-xs bg-gradient-to-br from-slate-800 to-slate-900 rounded-[2.5rem] p-3 shadow-2xl">
              <div className="bg-slate-900 rounded-[2rem] p-4 min-h-[380px] flex flex-col gap-3">
                <p className="text-center text-xs text-slate-400 font-medium mb-2">Suivi du trajet</p>
                {notifications.map((n, i) => (
                  <motion.div
                    key={n.title}
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.5, duration: 0.4 }}
                    className="bg-white/10 backdrop-blur rounded-xl p-3 flex items-center gap-3 border border-white/10"
                  >
                    <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center shrink-0">
                      {n.icon}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-white truncate">{n.title}</p>
                      <p className="text-xs text-slate-400">{n.time}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="lg:w-1/2 text-center lg:text-left"
          >
            <div className="inline-block px-3 py-1 bg-blue-50 text-blue-600 rounded-full text-sm font-semibold mb-4">
              POUR LES PARENTS
            </div>
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-6">
              Moins d&apos;attente, plus de sérénité.
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed max-w-xl mx-auto lg:mx-0">
              Du départ du bus à son arrivée à l&apos;école, les parents suivent chaque étape en direct — sans avoir à
              appeler ni deviner.
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// --- Expérience Chauffeur (Premium) ---
const DriverExperienceSection = () => {
  const total = 18;
  const [boarded, handleEnter] = useCountUp(total, { duration: 1.8 });
  const stops = ['Hay Riad', 'Founty', 'Massira', 'Centre-ville'];

  return (
    <section className="py-24 bg-slate-50 overflow-hidden">
      <div className="container mx-auto px-4">
        <div className="flex flex-col lg:flex-row items-center gap-16">
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="lg:w-1/2 text-center lg:text-left"
          >
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-orange-50 text-orange-600 rounded-full text-sm font-semibold mb-4">
              <Zap className="h-3.5 w-3.5 fill-orange-500" /> POUR LES CHAUFFEURS — PREMIUM
            </div>
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-6">
              Les bonnes informations, au bon moment.
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed max-w-xl mx-auto lg:mx-0">
              Avec l&apos;app chauffeur M&apos;EyeBus (offre Premium), chaque trajet est pointé arrêt par arrêt : liste
              des élèves, présence en temps réel, signalement en un geste.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, amount: 0.5 }}
            onViewportEnter={handleEnter}
            transition={{ duration: 0.6 }}
            className="lg:w-1/2"
          >
            <div className="mx-auto max-w-sm bg-white rounded-3xl shadow-2xl border border-slate-100 p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-xs text-slate-400 font-medium">Trajet du matin</p>
                  <p className="text-lg font-bold text-slate-900">08:12</p>
                </div>
                <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-orange-50 text-orange-600">
                  PREMIUM
                </span>
              </div>
              <div className="bg-slate-50 rounded-2xl p-4 mb-4 text-center">
                <p className="text-3xl font-bold text-slate-900">
                  {boarded}
                  <span className="text-slate-400">/{total}</span>
                </p>
                <p className="text-xs text-slate-500 mt-1">élèves à bord · 7 arrêts</p>
                <div className="h-1.5 w-full bg-slate-200 rounded-full overflow-hidden mt-3">
                  <div
                    className="h-full bg-gradient-to-r from-orange-400 to-orange-500 rounded-full transition-[width] duration-300"
                    style={{ width: `${(boarded / total) * 100}%` }}
                  />
                </div>
              </div>
              <ul className="space-y-2">
                {stops.map((s) => (
                  <li key={s} className="flex items-center gap-3 text-sm text-slate-700">
                    <CheckCircle2 className="h-4 w-4 text-green-500 shrink-0" />
                    Arrêt {s}
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// --- Benefits ---
const BenefitsSection = () => {
  const benefits = [
    {
      icon: <MapPin className="h-6 w-6 text-blue-600" />,
      bg: 'bg-blue-50',
      title: 'Visibilité',
      desc: 'Suivez vos bus en temps réel, sur toute la flotte.',
    },
    {
      icon: <Bell className="h-6 w-6 text-orange-600" />,
      bg: 'bg-orange-50',
      title: 'Communication',
      desc: 'Informez les parents automatiquement, à chaque étape du trajet.',
    },
    {
      icon: <LayoutDashboard className="h-6 w-6 text-indigo-600" />,
      bg: 'bg-indigo-50',
      title: 'Organisation',
      desc: 'Centralisez vos trajets, vos véhicules et vos chauffeurs au même endroit.',
    },
    {
      icon: <ShieldCheck className="h-6 w-6 text-green-600" />,
      bg: 'bg-green-50',
      title: 'Sérénité',
      desc: 'Offrez aux familles une meilleure expérience du transport scolaire.',
    },
  ];

  return (
    <section className="py-24 bg-white overflow-hidden" id="benefits">
      <div className="container mx-auto px-4">
        <div className="flex flex-col lg:flex-row items-center gap-16">
          {/* Visual */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="lg:w-1/2 relative"
          >
            <div className="absolute -inset-4 bg-orange-100 rounded-3xl transform -rotate-2" />
            <div className="relative rounded-2xl shadow-2xl overflow-hidden bg-gradient-to-br from-slate-800 to-slate-900 p-8">
              <div className="grid grid-cols-2 gap-4">
                {[
                  {
                    icon: <Bus className="h-8 w-8 text-blue-400" />,
                    value: 12,
                    suffix: ' bus actifs',
                    sub: 'En ce moment',
                  },
                  {
                    icon: <Users className="h-8 w-8 text-green-400" />,
                    value: 284,
                    suffix: ' élèves',
                    sub: "À bord aujourd'hui",
                  },
                  {
                    icon: <Bell className="h-8 w-8 text-orange-400" />,
                    value: 1204,
                    suffix: ' notifs',
                    sub: 'Envoyées ce mois',
                  },
                  {
                    icon: <TrendingDown className="h-8 w-8 text-purple-400" />,
                    value: 68,
                    prefix: '-',
                    suffix: '% appels',
                    sub: 'vs mois dernier',
                  },
                ].map((card, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1 }}
                    className="bg-white/10 backdrop-blur rounded-2xl p-4 border border-white/10"
                  >
                    <div className="mb-3">{card.icon}</div>
                    <p className="text-white font-bold text-lg leading-tight">
                      <Counter value={card.value} prefix={card.prefix} suffix={card.suffix} />
                    </p>
                    <p className="text-slate-400 text-xs mt-1">{card.sub}</p>
                  </motion.div>
                ))}
              </div>
              <div className="mt-4 bg-white/5 rounded-2xl p-4 border border-white/10">
                <div className="flex items-center gap-3 mb-3">
                  <span className="w-2.5 h-2.5 bg-green-400 rounded-full animate-pulse" />
                  <span className="text-white text-sm font-semibold">Bus #07 · Circuit Hay Riad</span>
                </div>
                <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                  <motion.div
                    initial={{ width: '0%' }}
                    whileInView={{ width: '65%' }}
                    viewport={{ once: true }}
                    transition={{ duration: 1.5, delay: 0.5 }}
                    className="h-full bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full"
                  />
                </div>
                <div className="flex justify-between mt-2 text-xs text-slate-400">
                  <span>Départ école</span>
                  <span>ETA 8 min</span>
                </div>
              </div>
            </div>

            <div className="absolute -bottom-6 -right-6 bg-white p-5 rounded-2xl shadow-xl border border-slate-100 max-w-[200px]">
              <div className="flex items-center gap-3 mb-2">
                <div className="bg-green-100 p-2.5 rounded-xl">
                  <ShieldCheck className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="font-bold text-slate-900 text-sm">Sécurité Max</p>
                  <p className="text-xs text-slate-500">Priorité #1</p>
                </div>
              </div>
              <p className="text-xs text-slate-600 leading-relaxed">Chaque trajet est monitoré en temps réel.</p>
            </div>
          </motion.div>

          {/* Content */}
          <div className="lg:w-1/2">
            <div className="inline-block px-3 py-1 bg-orange-50 text-orange-600 rounded-full text-sm font-semibold mb-6">
              POURQUOI NOUS CHOISIR ?
            </div>
            <h2 className="text-4xl font-bold text-slate-900 mb-8 leading-tight">
              Plus qu'un simple tracking, <span className="text-orange-500">une révolution de confiance.</span>
            </h2>

            <div className="space-y-8">
              {benefits.map((item, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  whileHover={{ x: 4 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.15 }}
                  className="flex gap-4"
                >
                  <div className={`flex-shrink-0 w-12 h-12 ${item.bg} rounded-xl flex items-center justify-center`}>
                    {item.icon}
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-slate-900 mb-2">{item.title}</h4>
                    <p className="text-slate-600 leading-relaxed">{item.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// --- Pricing ---
const PricingSection = () => {
  const basicFeatures = [
    'Suivi GPS temps réel (boîtier IoT ST901)',
    'Carte live pour les parents',
    'ETA dynamique',
    'Notification approche arrêt (150 m)',
    'Notification départ / arrivée école',
    'Dashboard admin web complet',
    'Gestion flotte, circuits & arrêts',
    "Jusqu'à 100 élèves · 5 chauffeurs",
    'Rétention GPS 30 jours',
    'Support 24/7 inclus',
  ];

  const premiumExtras = [
    'App mobile chauffeur',
    'Confirmation montée élève à bord',
    "Confirmation descente à l'arrêt",
    'Déclaration absence (2 taps)',
    'Historique trajets 365 jours',
    'Partage de trajet (tuteurs)',
    'Dashboard analytique avancé',
    'Rapports & statistiques de flotte',
    'Signalement incident chauffeur',
    "Jusqu'à 300 élèves · 20 chauffeurs",
  ];

  return (
    <section className="py-24 bg-slate-50" id="pricing">
      <div className="container mx-auto px-4">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <div className="inline-block px-3 py-1 bg-blue-50 text-blue-600 rounded-full text-sm font-semibold mb-4">
            NOS OFFRES
          </div>
          <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">Deux offres, un seul objectif.</h2>
          <p className="text-lg text-slate-600">Choisissez l'offre adaptée aux besoins de votre établissement.</p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {/* Basic */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-white rounded-3xl p-8 border-2 border-slate-200 shadow-sm flex flex-col"
          >
            <div className="mb-8">
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-slate-100 rounded-full text-slate-600 text-sm font-semibold mb-4">
                <Bus className="h-4 w-4" /> BASIC
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-2">Traceur GPS</h3>
              <p className="text-slate-500">L'essentiel pour savoir où est le bus à chaque instant.</p>
            </div>

            <ul className="space-y-3 mb-10 flex-grow">
              {basicFeatures.map((f) => (
                <li key={f} className="flex items-start gap-3 text-sm text-slate-700">
                  <CheckCircle2 className="h-5 w-5 text-blue-500 shrink-0 mt-0.5" />
                  {f}
                </li>
              ))}
            </ul>

            <Button
              onClick={scrollToContact}
              variant="outline"
              className="w-full !h-auto border-2 border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white py-6 rounded-xl font-bold text-base transition-all"
            >
              Demander une démo
            </Button>
          </motion.div>

          {/* Premium */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="bg-gradient-to-br from-blue-600 to-indigo-700 rounded-3xl p-8 text-white shadow-2xl shadow-blue-200 flex flex-col relative overflow-hidden"
          >
            <div className="absolute top-0 right-0 w-48 h-48 bg-white/10 rounded-bl-full pointer-events-none" />

            <div className="mb-8 relative z-10">
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/20 backdrop-blur rounded-full text-white text-sm font-semibold mb-4">
                <Zap className="h-4 w-4 fill-white" /> PREMIUM — Recommandé
              </div>
              <h3 className="text-2xl font-bold mb-2">Traceur GPS + App Chauffeur</h3>
              <p className="text-blue-100">La confirmation que votre enfant est bien monté à bord.</p>
            </div>

            <div className="relative z-10 mb-8 flex-grow">
              <p className="text-sm text-blue-200 font-semibold mb-4 uppercase tracking-wider">Tout le Basic, plus :</p>
              <ul className="space-y-3">
                {premiumExtras.map((f) => (
                  <li key={f} className="flex items-start gap-3 text-sm text-white">
                    <CheckCircle2 className="h-5 w-5 text-blue-300 shrink-0 mt-0.5" />
                    {f}
                  </li>
                ))}
              </ul>
            </div>

            <Button
              onClick={scrollToContact}
              className="w-full !h-auto bg-white text-blue-700 hover:bg-blue-50 py-6 rounded-xl font-bold text-base shadow-lg relative z-10"
            >
              Demander une démo <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </motion.div>
        </div>

        <p className="text-center text-sm text-slate-500 mt-8">
          Tarifs sur devis · Installation incluse · Support 24/7 · Sans engagement
        </p>
      </div>
    </section>
  );
};

// --- App Download ---
const AppDownloadSection = () => (
  <section className="py-20 bg-white border-y border-slate-100" id="download">
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
      className="container mx-auto px-4 max-w-2xl text-center"
    >
      <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-blue-100 text-blue-600 mb-6">
        <Smartphone className="h-7 w-7" />
      </div>
      <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
        Téléchargez l&apos;application M&apos;EyeBus
      </h2>
      <p className="text-lg text-slate-600 mb-10">
        Suivez les trajets, recevez les alertes et gérez le transport scolaire de vos enfants depuis votre mobile.
      </p>
      <div className="mx-auto flex w-full max-w-[196px] flex-col items-stretch gap-3 sm:max-w-none sm:flex-row sm:items-center sm:justify-center sm:gap-6">
        <StoreBadgeLink href={STORE_LINKS.appStore} ariaLabel="Télécharger M'EyeBus sur l'App Store">
          <AppStoreBadge />
        </StoreBadgeLink>
        <StoreBadgeLink href={STORE_LINKS.googlePlay} ariaLabel="Télécharger M'EyeBus sur Google Play">
          <GooglePlayBadge />
        </StoreBadgeLink>
      </div>
    </motion.div>
  </section>
);

// --- Contact ---
const ContactSection = () => {
  const [formState, setFormState] = useState({
    name: '',
    email: '',
    phone: '',
    school: '',
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => setFormState((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      // apiRequest rejette sur tout statut non-2xx : si on arrive ici, c'est un succès.
      await unifiedApi.public.submitContact(formState);
      toast.success('Demande envoyée ! Nous vous recontactons sous 24h.');
      setFormState({ name: '', email: '', phone: '', school: '', message: '' });
    } catch (err) {
      toast.error(err.message || "Erreur lors de l'envoi, réessayez ou contactez-nous par WhatsApp.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="py-24 bg-slate-900 text-white relative overflow-hidden" id="contact">
      <div className="absolute top-[-10%] right-[-5%] w-[500px] h-[500px] bg-blue-600 rounded-full blur-[120px] opacity-20 pointer-events-none" />
      <div className="absolute bottom-[-10%] left-[-5%] w-[500px] h-[500px] bg-indigo-600 rounded-full blur-[120px] opacity-20 pointer-events-none" />

      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-4xl mx-auto bg-white/5 backdrop-blur-lg border border-white/10 rounded-3xl p-8 md:p-16 shadow-2xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Prêt à transformer votre transport scolaire ?</h2>
            <p className="text-slate-300 text-lg">
              Rejoignez les écoles innovantes qui ont choisi la sécurité et la sérénité.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-300">Nom complet</label>
                <input
                  type="text"
                  name="name"
                  value={formState.name}
                  onChange={handleChange}
                  className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                  placeholder="Nom complet"
                  required
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-300">Email professionnel</label>
                <input
                  type="email"
                  name="email"
                  value={formState.email}
                  onChange={handleChange}
                  className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                  placeholder="mail@ecole.com"
                  required
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-300">Nom de l'établissement</label>
                <input
                  type="text"
                  name="school"
                  value={formState.school}
                  onChange={handleChange}
                  className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                  placeholder="Groupe Scolaire..."
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-300">
                  Téléphone <span className="text-slate-500">(optionnel)</span>
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formState.phone}
                  onChange={handleChange}
                  className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                  placeholder="+212 6XX XXX XXX"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-300">Message</label>
              <textarea
                name="message"
                value={formState.message}
                onChange={handleChange}
                className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all h-32 resize-none"
                placeholder="Dites-nous en plus sur vos besoins..."
                required
              />
            </div>

            <div className="pt-2 flex flex-col sm:flex-row gap-3">
              <Button
                type="submit"
                disabled={submitting}
                className="w-full sm:flex-1 !h-auto bg-blue-600 hover:bg-blue-700 text-white py-6 text-lg rounded-xl font-bold shadow-lg shadow-blue-900/50 transition-all hover:scale-[1.02] disabled:opacity-60 disabled:hover:scale-100"
              >
                {submitting ? 'Envoi en cours...' : 'Demander ma démo'}
              </Button>
              <WhatsAppButton
                variant="outline"
                message="Bonjour, je souhaite en savoir plus sur MeyeBus pour mon établissement."
                className="w-full sm:flex-1 !py-6 text-lg bg-white/5"
              >
                WhatsApp
              </WhatsAppButton>
            </div>

            <p className="text-center text-xs text-slate-400">Sans engagement · Réponse sous 24h</p>
          </form>
        </div>
      </div>
    </section>
  );
};

// --- Footer ---
const Footer = () => (
  <footer className="bg-slate-50 py-14 border-t border-slate-200">
    <div className="container mx-auto px-4">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-10 mb-10">
        <div className="md:col-span-2">
          <div className="flex items-center gap-2 mb-4">
            <img src="/logo.png" alt="M'EyeBus" className="h-10 w-auto" />
            <img src="/logo_trademark.png" alt="M'EyeBus" className="h-5 w-auto object-contain" />
          </div>
          <p className="text-slate-500 text-sm leading-relaxed max-w-xs">
            La plateforme de transport scolaire connecté qui protège vos enfants et libère vos équipes.
          </p>
          <div className="flex items-center gap-2 mt-4 text-slate-500 text-sm">
            <Mail className="h-4 w-4" />
            <a href="mailto:contact@meyebus.ma" className="hover:text-blue-600 transition-colors">
              contact@meyebus.ma
            </a>
          </div>
          <div className="mt-2">
            <WhatsAppButton variant="link" className="text-sm font-normal">
              WhatsApp
            </WhatsAppButton>
          </div>
          <SocialLinks className="mt-5" itemClassName="h-10 w-10 rounded-full border" />
        </div>

        <div>
          <p className="text-sm font-semibold text-slate-900 mb-4 uppercase tracking-wider">Produit</p>
          <ul className="space-y-3">
            {[
              { href: '#features', label: 'Fonctionnalités' },
              { href: '#how-it-works', label: 'Comment ça marche' },
              { href: '#pricing', label: 'Tarifs' },
              { href: '#download', label: 'Application mobile' },
            ].map((l) => (
              <li key={l.href}>
                <a href={l.href} className="text-sm text-slate-500 hover:text-blue-600 transition-colors">
                  {l.label}
                </a>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <p className="text-sm font-semibold text-slate-900 mb-4 uppercase tracking-wider">Légal</p>
          <ul className="space-y-3">
            <li>
              <Link to="/privacy-policy" className="text-sm text-slate-500 hover:text-blue-600 transition-colors">
                Confidentialité
              </Link>
            </li>
            <li>
              <Link to="/faq" className="text-sm text-slate-500 hover:text-blue-600 transition-colors">
                FAQ
              </Link>
            </li>
          </ul>
        </div>
      </div>

      <div className="border-t border-slate-200 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
        <div className="flex items-center gap-1.5">
          <img src="/logo_trademark_corp.png" alt="M'EyeBus" className="h-5 w-auto object-contain" />
          <span className="text-sm text-slate-500">
            © <span className="font-semibold text-slate-700">2026</span> M'EyeBus · Tous droits réservés.
          </span>
        </div>
        <div className="flex items-center gap-2">
          <StoreBadgeLink href={STORE_LINKS.appStore} ariaLabel="App Store">
            <AppStoreBadge />
          </StoreBadgeLink>
          <StoreBadgeLink href={STORE_LINKS.googlePlay} ariaLabel="Google Play">
            <GooglePlayBadge />
          </StoreBadgeLink>
        </div>
      </div>
    </div>
  </footer>
);

// --- Main ---
// Ordre des sections de <main> = ordre des arrêts du fil de scroll (le libellé s'affiche à côté du bus).
const LANDING_SECTIONS = [
  { Component: HeroSection, label: 'Accueil' },
  { Component: StatsBar, label: 'Chiffres clés' },
  { Component: ProblemSection, label: 'Le problème' },
  { Component: FeaturesSection, label: 'Fonctionnalités' },
  { Component: ParentExperienceSection, label: 'Côté parents' },
  { Component: DriverExperienceSection, label: 'Côté chauffeur' },
  { Component: BenefitsSection, label: 'Bénéfices' },
  { Component: HowItWorksSection, label: 'Comment ça marche' },
  { Component: PricingSection, label: 'Tarifs' },
  { Component: AppDownloadSection, label: "L'application" },
  { Component: ContactSection, label: 'Demander une démo' },
];

export default function LandingPage() {
  const mainRef = useRef(null);

  return (
    <div className="min-h-screen bg-white font-sans selection:bg-blue-100 selection:text-blue-900">
      <Helmet>
        <title>M'EyeBus — Suivi GPS et gestion du transport scolaire au Maroc</title>
        <meta
          name="description"
          content="M'EyeBus connecte écoles, chauffeurs et parents pour suivre le transport scolaire en temps réel : suivi GPS, notifications parents, gestion de flotte. Demandez une démo."
        />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="M'EyeBus — Le transport scolaire, enfin sécurisé" />
        <meta
          property="og:description"
          content="Suivi GPS temps réel, notifications parents et gestion de flotte pour les écoles au Maroc."
        />
      </Helmet>
      <Navbar />
      <main ref={mainRef}>
        {LANDING_SECTIONS.map(({ Component, label }) => (
          <Component key={label} />
        ))}
      </main>
      <Footer />
      <ScrollThread targetRef={mainRef} labels={LANDING_SECTIONS.map((s) => s.label)} />
    </div>
  );
}
